import { cva } from 'class-variance-authority';
import { Form, Formik } from 'formik';
import { useTranslation } from 'next-i18next';
import { twMerge } from 'tailwind-merge';
import * as Yup from 'yup';

import { Alert, Link } from '@boss/ui';

import Checkbox from './Checkbox';
import FormField from './FormField';
import { useFormSubmit } from './formSubmit';

export type ContactFormValues = {
  email: string;
  firstname: string;
  lastname: string;
  servicemessage: string;
  phone: string;
  type: string;
};

type ErrorProps = {
  error: string;
};

const ButtonStyle = cva('p-4 uppercase text-white', {
  variants: {
    isEnabled: {
      true: 'hover:border-1 bg-black hover:border-solid hover:border-black hover:bg-white hover:text-black',
      false: 'bg-gray-medium cursor-not-allowed',
    },
  },
});

const Error = ({ error }: ErrorProps) => <span className="annotation text-red absolute bottom-0 mt-2">{error}</span>;

const ContactForm = ({ textClassName }: { textClassName?: string }) => {
  const { t } = useTranslation(['contact', 'common']);
  const itemClassName = 'relative flex flex-col pb-6 justify-end';
  const requireMessage = t('form.required');
  const validMessage = t('form.validMail');
  const { onSubmit, isSubmitting, isSuccess, reset } = useFormSubmit();

  const contactSchema = Yup.object().shape({
    email: Yup.string().email(validMessage).required(requireMessage),
    firstname: Yup.string().required(requireMessage),
    lastname: Yup.string().required(requireMessage),
    servicemessage: Yup.string().required(requireMessage),
    privacy: Yup.boolean().oneOf([true], requireMessage),
  });

  const handleSubmit = (values: ContactFormValues) => {
    const { type, firstname, lastname, email, servicemessage, phone } = values;

    onSubmit({
      firstname,
      lastname,
      phone,
      servicemessage,
      type,
      email,
    });
  };

  return (
    <div className={textClassName}>
      <h2 className="h2 mb-7.5">{t('form.title')}</h2>
      {isSuccess ? (
        <Alert
          className="mt-5"
          iconSize="xl"
          onClose={reset}
          title={t('toast.success.title', { ns: 'common' })}
          type="confirm"
        >
          {t('toast.success.description', { ns: 'common' })}
        </Alert>
      ) : (
        <Formik
          initialValues={{
            firstname: '',
            lastname: '',
            email: '',
            phone: '',
            servicemessage: '',
            type: 'GeneralQuestion',
            privacy: false,
          }}
          isSubmitting={isSubmitting}
          isSuccess={isSuccess}
          onSubmit={handleSubmit}
          validationSchema={contactSchema}
        >
          {({ errors, touched, values }) => (
            <Form>
              <div className="grid w-full grid-cols-2 gap-6">
                <div className={itemClassName}>
                  <FormField label={t('form.fields.firstName')} name="firstname" />
                  {errors.firstname && touched.firstname ? <Error error={errors.firstname} /> : null}
                </div>
                <div className={itemClassName}>
                  <FormField label={t('form.fields.lastName')} name="lastname" />
                  {errors.lastname && touched.lastname ? <Error error={errors.lastname} /> : null}
                </div>
                <div className={itemClassName}>
                  <FormField label={t('form.fields.email')} name="email" />
                  {errors.email && touched.email ? <Error error={errors.email} /> : null}
                </div>
                <div className={itemClassName}>
                  <FormField inputMode="tel" label={t('form.fields.phoneNumber')} name="phone" type="tel" />
                </div>
                <div className={twMerge(itemClassName, 'col-span-2')}>
                  <FormField fieldClassName="h-75" label={t('form.fields.message')} name="servicemessage" textArea />
                  {errors.servicemessage && touched.servicemessage ? <Error error={errors.servicemessage} /> : null}
                </div>
                <div className={twMerge(itemClassName, 'col-span-2 mb-8')}>
                  <Checkbox labelClassName="flex flex-row items-center mr-2" name="privacy">
                    <span>
                      {t('form.fields.privacyFirst')} &nbsp;
                      <span className="underline">
                        <Link href={t('form.fields.privacyLink')}>{t('form.fields.privacyLinkText')}</Link>
                      </span>
                      &nbsp;
                      {t('form.fields.privacyLast')}
                    </span>
                  </Checkbox>
                  {errors.privacy && touched.privacy ? <Error error={errors.privacy} /> : null}
                </div>
              </div>
              <button className={ButtonStyle({ isEnabled: Object.keys(errors).length <= 0 })} type="submit">
                {t('form.submit')}
              </button>
            </Form>
          )}
        </Formik>
      )}
    </div>
  );
};

export default ContactForm;
